import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { ChatIcon } from '../icons/chat'

export default function ChatManager (): JSX.Element {
  const { t } = useTranslation()
  const [chatAvailable, setChatAvailable] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const chatVisible = useMemo(() => chatAvailable && chatOpen, [chatAvailable, chatOpen])
  const buttonVisible = useMemo(() => chatAvailable && !chatOpen, [chatAvailable, chatOpen])
  const openChatRef = useRef<(() => Promise<void>) | null>(null)

  useEffect(() => {
    document.documentElement.classList.toggle('chat-visible', chatVisible)
  }, [chatVisible])

  useEffect(() => {
    const abort = new AbortController()

    window.addEventListener('onEmbeddedMessagingReady', () => {
      window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
        End_User_Language: window.embeddedservice_bootstrap.settings.language,
        SourceURL: window.location.href
      })
      setChatAvailable(true)
    }, { signal: abort.signal })

    window.addEventListener('onEmbeddedMessagingWindowClosed', () => {
      setChatOpen(false)
    })

    window.addEventListener('onEmbeddedMessagingWindowMinimized', () => {
      setChatOpen(false)
    })

    window.addEventListener('onEmbeddedMessagingWindowMaximized', () => {
      setChatOpen(true)
    })

    window.addEventListener('start-chat', () => {
      void openChatRef.current?.()
    })

    return () => {
      abort.abort()
    }
  }, [])

  const openChat = useCallback(async () => {
    await window.embeddedservice_bootstrap.utilAPI.launchChat()
    setChatOpen(true)
  }, [])

  useEffect(() => {
    openChatRef.current = openChat
  })

  return (
    <aside
      className={classNames('fixed z-999 bottom-24 lg:bottom-7 right-5 flex gap-4 items-center', { hidden: !buttonVisible })}
    >
      <button
        className='py-3 px-4 bg-success-500 rounded-full text-base text-white shadow-sm max-lg:hidden'
        type='button' onClick={() => {
          void openChat()
        }}
      >{t('chat_button_text', '💬 How can we help?')}
      </button>
      <button
        className='rounded-full size-12 relative text-base'
        type='button' onClick={() => {
          void openChat()
        }}
      >
        <ChatIcon className='size-[90px] absolute left-[-21px] right-[-21px] top-[-17px]' />
      </button>
    </aside>
  )
}
